import React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';
import { FaFacebook, FaGoogle, FaInstagram } from 'react-icons/fa';
import { API_SN_DELETE, API_GET_LINKS } from '../../../utils';
import './style.css';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import { initiateGoogleOAuth } from '../../../services/googleOAuth';
// Facebook OAuth is now handled fully server-side via ms-seo-audit APIs
import BingLogo from '../../../Icons/bing.svg';

import {
  Home,
  Star,
  FileText,
  Image,
  Bell,
  LogOut,
  Menu,
  Facebook,
  Instagram,
  Check,
  X,
  CalendarDays,
  Building2,
  Plus,
  Link2,
  CheckCircle2,
  Unplug,
  MapPin,
  Loader2,
  AlertCircle,
  ExternalLink,
  Save,
  Search
} from 'lucide-react';
import { SEO_AUDIT_ENABLED } from '../../../utils/constants';

const SEO_AUDIT_API =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

// Review platform definitions with icons, colors, fields and preview URL builders
const REVIEW_PLATFORMS = [
  {
    key: 'yelp',
    name: 'Yelp',
    color: 'bg-red-600',
    icon: (
      <svg width="20" height="20" viewBox="0 0 16 16" fill="white">
        <path d="m4.188 10.095.736-.17.073-.02A.813.813 0 0 0 5.45 8.65a1 1 0 0 0-.3-.258 3 3 0 0 0-.428-.198l-.808-.295a76 76 0 0 0-1.364-.493C2.253 7.3 2 7.208 1.783 7.14c-.041-.013-.087-.025-.124-.038a2.1 2.1 0 0 0-.606-.116.72.72 0 0 0-.572.245 2 2 0 0 0-.105.132 1.6 1.6 0 0 0-.155.309c-.15.443-.225.908-.22 1.376.002.423.013.966.246 1.334a.8.8 0 0 0 .22.24c.166.114.333.129.507.141.26.019.513-.045.764-.103l2.447-.566z" />
      </svg>
    ),
    fields: [
      {
        key: 'businessId',
        label: 'Business ID (slug)',
        placeholder: 'e.g. scotts-seafood-san-jose',
        required: true
      }
    ],
    howToFind:
      'Go to yelp.com → search your business → copy the slug from the URL: yelp.com/biz/YOUR-SLUG',
    getPreviewUrl: (data) =>
      data.businessId ? `https://www.yelp.com/biz/${data.businessId}` : null,
    isConnected: (data) => !!data?.businessId
  },
  {
    key: 'tripadvisor',
    name: 'TripAdvisor',
    color: 'bg-green-600',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
        <path d="M20 6.009h-2.829C15.211 4.675 12.813 4 10 4s-5.212.675-7.171 2.009H0c.428.42.827 1.34.993 2.04A4.954 4.954 0 0 0 0 11.008c0 2.757 2.243 5 5 5a4.97 4.97 0 0 0 3.423-1.375L10 17l1.577-2.366A4.97 4.97 0 0 0 15 16.01c2.757 0 5-2.243 5-5 0-1.112-.377-2.13-.993-2.96.166-.7.565-1.62.993-2.04z" />
      </svg>
    ),
    fields: [
      {
        key: 'tripAdvisorUrl',
        label: 'TripAdvisor Page URL',
        placeholder:
          'e.g. https://www.tripadvisor.com/Hotel_Review-g33388-d111561-Reviews-Queen_Anne...',
        required: true
      }
    ],
    howToFind:
      'Go to tripadvisor.com → search your business → open the page → copy the full URL from your browser address bar.',
    getPreviewUrl: (data) => data.tripAdvisorUrl || null,
    isConnected: (data) => !!data?.tripAdvisorUrl
  },
  {
    key: 'opentable',
    name: 'OpenTable',
    color: 'bg-red-500',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
      </svg>
    ),
    fields: [
      {
        key: 'restaurantUrl',
        label: 'OpenTable Restaurant URL',
        placeholder: 'e.g. https://www.opentable.com/r/scotts-seafood-san-jose',
        required: true
      }
    ],
    howToFind:
      'Go to opentable.com \u2192 search your restaurant \u2192 open the page \u2192 copy the full URL from the browser (works with both /r/ and non-/r/ URLs).',
    getPreviewUrl: (data) => {
      const url = data.restaurantUrl || '';
      if (url.startsWith('http')) return url.split('?')[0];
      return null;
    },
    isConnected: (data) => !!data?.restaurantUrl
  },
  {
    key: 'bing',
    name: 'Bing Maps',
    color: 'bg-teal-600',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        fill="white"
        viewBox="0 0 16 16">
        <path d="M8.35 5.046a.615.615 0 0 0-.54.575c-.009.13-.006.14.289.899.67 1.727.833 2.142.86 2.2q.101.215.277.395c.089.092.148.141.247.208.176.117.262.15.944.351.664.197 1.026.327 1.338.482.405.201.688.43.866.7.128.195.242.544.291.896.02.137.02.44 0 .564-.041.27-.124.495-.252.684-.067.1-.044.084.055-.039.278-.346.562-.938.707-1.475a4.42 4.42 0 0 0-2.14-5.028 70 70 0 0 0-.888-.465l-.53-.277-.353-.184c-.16-.082-.266-.138-.345-.18-.368-.192-.523-.27-.568-.283a1 1 0 0 0-.194-.03z" />
        <path d="M9.152 11.493a3 3 0 0 0-.135.083 320 320 0 0 0-1.513.934l-.8.496c-.012.01-.587.367-.876.543a1.9 1.9 0 0 1-.732.257c-.12.017-.349.017-.47 0a1.9 1.9 0 0 1-.884-.358 2.5 2.5 0 0 1-.365-.364 1.9 1.9 0 0 1-.34-.76 1 1 0 0 0-.027-.121c-.005-.006.004.092.022.22.018.132.057.324.098.489a4.1 4.1 0 0 0 2.487 2.796c.359.142.72.23 1.114.275.147.016.566.023.72.011a4.1 4.1 0 0 0 1.956-.661l.235-.149.394-.248.258-.163 1.164-.736c.51-.32.663-.433.9-.665.099-.097.248-.262.255-.283.002-.005.028-.046.059-.091a1.64 1.64 0 0 0 .25-.682c.02-.124.02-.427 0-.565a3 3 0 0 0-.213-.758c-.15-.314-.47-.6-.928-.83a2 2 0 0 0-.273-.12c-.006 0-.433.26-.948.58l-1.113.687z" />
        <path d="m3.004 12.184.03.129c.089.402.245.693.515.963a1.82 1.82 0 0 0 1.312.543c.361 0 .673-.09.994-.287l.472-.29.373-.23V5.334c0-1.537-.003-2.45-.008-2.521a1.82 1.82 0 0 0-.535-1.177c-.097-.096-.18-.16-.427-.33L4.183.24c-.239-.163-.258-.175-.33-.2a.63.63 0 0 0-.842.464c-.009.042-.01.603-.01 3.646l.003 8.035Z" />
      </svg>
    ),
    fields: [
      {
        key: 'query',
        label: 'Business Name + City',
        placeholder: 'e.g. Scotts Seafood San Jose',
        required: true
      }
    ],
    howToFind:
      'Enter your business name and city — Bing Maps will auto-find it. Note: Bing aggregates reviews from Yelp, TripAdvisor, etc.',
    getPreviewUrl: (data) =>
      data.query
        ? `https://www.bing.com/maps?q=${encodeURIComponent(data.query)}`
        : null,
    isConnected: (data) => !!data?.query
  },
  {
    key: 'angi',
    name: 'Angi',
    color: 'bg-green-700',
    icon: <span className="text-white font-bold text-xs">A</span>,
    fields: [
      {
        key: 'profileUrl',
        label: 'Angi Profile URL',
        placeholder: 'e.g. https://www.angi.com/companylist/us/ca/...',
        required: true
      }
    ],
    howToFind:
      'Go to angi.com → search your business → open your profile page → copy the full URL from the browser.',
    getPreviewUrl: (data) => data.profileUrl || null,
    isConnected: (data) => !!data?.profileUrl
  },
  {
    key: 'bbb',
    name: 'BBB',
    color: 'bg-blue-800',
    icon: <span className="text-white font-bold text-xs">BBB</span>,
    fields: [
      {
        key: 'businessUrl',
        label: 'BBB Business URL',
        placeholder: 'e.g. https://www.bbb.org/us/ca/san-jose/profile/...',
        required: true
      },
      {
        key: 'businessName',
        label: 'Business Name',
        placeholder: 'e.g. Scotts Seafood',
        required: false
      },
      {
        key: 'location',
        label: 'Location',
        placeholder: 'e.g. San Jose, CA',
        required: false
      }
    ],
    howToFind:
      'Go to bbb.org → search your business → open your profile page → copy the full URL from the browser.',
    getPreviewUrl: (data) => data.businessUrl || null,
    isConnected: (data) => !!data?.businessUrl
  }
];

const normalizeReviewPlatforms = (
  basePlatforms = {},
  fallbackPlatforms = {},
  selectedClient = {}
) => {
  const merged = {
    ...basePlatforms,
    ...fallbackPlatforms
  };

  const yelp = merged.yelp || {};
  const facebook = merged.facebook || {};
  const tripadvisor = merged.tripadvisor || {};
  const bing = merged.bing || {};
  const bbb = merged.bbb || {};
  const opentable = merged.opentable || {};
  const angi = merged.angi || {};

  return {
    yelp: {
      businessId: yelp.businessId || selectedClient?.yelpId || '',
      businessName: yelp.businessName || '',
      location: yelp.location || ''
    },
    facebook: {
      pageUrl:
        facebook.pageUrl ||
        (facebook.pageId ? `https://facebook.com/${facebook.pageId}` : ''),
      pageId: facebook.pageId || '',
      accessToken: facebook.accessToken || '',
      pageName: facebook.pageName || '',
      configured: !!(facebook.pageId && facebook.accessToken)
    },
    tripadvisor: {
      tripAdvisorUrl:
        tripadvisor.tripAdvisorUrl ||
        tripadvisor.urlPath ||
        tripadvisor.url ||
        ''
    },
    bing: {
      query:
        bing.query ||
        `${
          selectedClient?.clientName ||
          selectedClient?.mergeFields?.['~Name'] ||
          ''
        } ${selectedClient?.mergeFields?.['~City'] || ''}`.trim()
    },
    bbb: {
      businessUrl: bbb.businessUrl || bbb.url || '',
      businessName: bbb.businessName || '',
      location: bbb.location || ''
    },
    opentable: {
      restaurantUrl:
        opentable.restaurantUrl || opentable.url || opentable.restaurantId || ''
    },
    angi: {
      profileUrl: angi.profileUrl || angi.url || ''
    }
  };
};

const Links = ({ selectedClient, userInfo }) => {
  const history = useHistory();
  const [links, setLinks] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectNetwork, setDisconnectNetwork] = useState('');
  const [disconnectClientId, setDisconnectClientId] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── Review Platform State ──────────────────────────────────
  const [reviewPlatforms, setReviewPlatforms] = useState({
    yelp: { businessId: '', businessName: '', location: '' },
    facebook: {
      pageUrl: '',
      pageId: '',
      accessToken: '',
      pageName: '',
      configured: false
    },
    tripadvisor: {
      tripAdvisorUrl: ''
    },
    bing: { query: '' },
    bbb: { businessUrl: '', businessName: '', location: '' },
    opentable: { restaurantUrl: '' },
    angi: { profileUrl: '' }
  });
  const [reviewSettingsLoaded, setReviewSettingsLoaded] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configPlatform, setConfigPlatform] = useState(null);
  const [configFormData, setConfigFormData] = useState({});
  const [isSavingPlatform, setIsSavingPlatform] = useState(false);
  const [reviewDisconnectModalOpen, setReviewDisconnectModalOpen] =
    useState(false);
  const [reviewDisconnectPlatform, setReviewDisconnectPlatform] =
    useState(null);
  const [isDisconnectingReview, setIsDisconnectingReview] = useState(false);

  // Listener para mensajes del popup de OAuth (Google only now)
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
        console.log('Google OAuth completed successfully, reloading page...');
        window.location.reload();
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, []);

  // Función para iniciar OAuth de Google
  const handleConnectGoogle = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_GOOGLE_OAUTH_REDIRECT_URI;
    const state = encodeURIComponent(
      JSON.stringify({ clientId: selectedClient.id, platform: 'adlersuite' })
    );
    initiateGoogleOAuth(clientId, redirectUri, state);
  };

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    setErrorMessage('');

    try {
      let response;
      if (disconnectNetwork === 'facebook') {
        // Use the ms-seo-audit backend to remove the platform
        const url = `https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev/reviews/settings/platforms/${disconnectNetwork}`;
        response = await axios.delete(url, {
          data: { locationId: disconnectClientId },
          timeout: 30000,
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        response = await axios.delete(API_SN_DELETE, {
          data: {
            socialNetwork: disconnectNetwork,
            id: disconnectClientId
          },
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.status === 200) {
        // If Facebook, clear the local reviewPlatforms state immediately
        if (disconnectNetwork === 'facebook') {
          setReviewPlatforms((prev) => ({
            ...prev,
            facebook: {
              pageUrl: '',
              pageId: '',
              accessToken: '',
              pageName: '',
              configured: false
            }
          }));
        }
        setDisconnectModalOpen(false);
        setSuccessModalOpen(true);
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error('🔴 [DISCONNECT] Error occurred:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code,
        isTimeout: error.code === 'ECONNABORTED',
        timestamp: new Date().toISOString(),
        fullError: error
      });

      setIsDisconnecting(false);
      setDisconnectModalOpen(false);

      // Mensajes de error más descriptivos
      let userMessage = 'Failed to disconnect the social network. ';

      if (error.code === 'ECONNABORTED') {
        userMessage +=
          'The request timed out after 30 seconds. Please try again.';
      } else if (error.response?.status === 504) {
        userMessage +=
          'Gateway timeout (504). The server took too long to respond. Please try again in a few moments.';
      } else if (error.response?.status === 500) {
        userMessage +=
          'Internal server error (500). Please contact support if this persists.';
      } else if (error.response?.data?.error) {
        userMessage += error.response.data.error;
      } else if (error.message) {
        userMessage += error.message;
      } else {
        userMessage += 'An unknown error occurred. Please try again.';
      }

      setErrorMessage(userMessage);
      setErrorModalOpen(true);
      return;
    }

    setIsDisconnecting(false);
  };

  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
    history.go(0);
  };

  useEffect(() => {
    if (!links) {
      const clientSocialNetworksLinks = async (clientId) => {
        try {
          const { Links } = await (
            await fetch(API_GET_LINKS + new URLSearchParams({ clientId }))
          ).json();
          if (links === null) {
            setLinks(Links);
          }
        } catch (error) {
          console.error('The link have an error', error);
        }
      };
      if (selectedClient.id && links === null) {
        clientSocialNetworksLinks(selectedClient.id);
      }
    }
  }, [selectedClient.id, links]);

  // ── Fetch Review Settings on mount ────────────────────────
  useEffect(() => {
    const fetchReviewSettings = async () => {
      const locationId = selectedClient?.id || selectedClient?.currentClientId;
      if (!locationId) return;
      try {
        const res = await axios.get(`${SEO_AUDIT_API}/reviews/settings`, {
          params: { locationId }
        });
        const saved = res.data?.settings || {};
        const savedPlatforms = saved.reviewPlatforms || {};
        setReviewPlatforms(
          normalizeReviewPlatforms(
            savedPlatforms,
            selectedClient?.reviewPlatforms || {},
            selectedClient
          )
        );
      } catch (err) {
        console.error('Failed to load review settings:', err);
        setReviewPlatforms(
          normalizeReviewPlatforms(
            selectedClient?.reviewPlatforms || {},
            {},
            selectedClient
          )
        );
      } finally {
        setReviewSettingsLoaded(true);
      }
    };
    fetchReviewSettings();
  }, [selectedClient?.id, selectedClient?.currentClientId]);

  // ── Review Platform Handlers ──────────────────────────────
  const openConfigModal = (platform) => {
    setConfigPlatform(platform);
    setConfigFormData({ ...(reviewPlatforms[platform.key] || {}) });
    setConfigModalOpen(true);
  };

  const handleConfigFieldChange = (fieldKey, value) => {
    setConfigFormData((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSavePlatformConfig = async () => {
    if (!configPlatform) return;
    setIsSavingPlatform(true);
    const locationId = selectedClient?.id || selectedClient?.currentClientId;

    const updatedPlatforms = {
      ...reviewPlatforms,
      [configPlatform.key]: {
        ...reviewPlatforms[configPlatform.key],
        ...configFormData
      }
    };

    try {
      await axios.post(`${SEO_AUDIT_API}/reviews/settings`, {
        locationId,
        reviewPlatforms: updatedPlatforms
      });
      setReviewPlatforms(updatedPlatforms);
      setConfigModalOpen(false);
      setConfigPlatform(null);
    } catch (err) {
      console.error('Failed to save platform config:', err);
      setErrorMessage(
        'Failed to save platform configuration. Please try again.'
      );
      setErrorModalOpen(true);
    } finally {
      setIsSavingPlatform(false);
    }
  };

  const handleDisconnectReviewPlatform = async () => {
    if (!reviewDisconnectPlatform) return;
    setIsDisconnectingReview(true);
    const locationId = selectedClient?.id || selectedClient?.currentClientId;
    const platformKey = reviewDisconnectPlatform.key;

    // Build a cleared version of the platform data
    const clearedFields = {};
    reviewDisconnectPlatform.fields.forEach((f) => {
      clearedFields[f.key] = '';
    });

    const updatedPlatforms = {
      ...reviewPlatforms,
      [platformKey]: clearedFields
    };

    try {
      await axios.post(`${SEO_AUDIT_API}/reviews/settings`, {
        locationId,
        reviewPlatforms: updatedPlatforms
      });
      setReviewPlatforms(updatedPlatforms);
      setReviewDisconnectModalOpen(false);
      setReviewDisconnectPlatform(null);
    } catch (err) {
      console.error('Failed to disconnect platform:', err);
      setErrorMessage('Failed to disconnect platform. Please try again.');
      setErrorModalOpen(true);
      setReviewDisconnectModalOpen(false);
    } finally {
      setIsDisconnectingReview(false);
    }
  };

  return (
    <>
      <Card className="shadow-md rounded-2xl border-0">
        <CardHeader className="flex flex-row items-center justify-between pb-4 pt-6 px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Link2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl lg:text-2xl font-bold">
                Connected Accounts
              </CardTitle>
              <CardDescription className="text-base mt-1">
                Your social media and business listings
              </CardDescription>
            </div>
          </div>

          <div className="relative" style={{ display: 'none' }}>
            <Button
              type="button"
              size="lg"
              onClick={() => setShowDropdown(!showDropdown)}
              className="gap-3 h-12 px-6 text-base font-semibold rounded-xl">
              <Plus className="h-5 w-5" />
              Add Account
            </Button>

            {showDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-50 p-2">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors">
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <Facebook className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">Facebook</span>
                  </button>

                  <div className="h-px bg-gray-200 my-2" />

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center overflow-hidden">
                      <img
                        src={BingLogo}
                        alt="Bing"
                        className="h-6 w-6 object-contain"
                      />
                    </div>
                    <span className="font-medium">Bing Places</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                      <svg
                        className="h-4 w-4 text-white"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                      </svg>
                    </div>
                    <span className="font-medium">TikTok</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 flex items-center justify-center text-black">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor">
                        <path d="M20 6.009h-2.829C15.211 4.675 12.813 4 10 4s-5.212.675-7.171 2.009H0c.428.42.827 1.34.993 2.04A4.954 4.954 0 0 0 0 11.008c0 2.757 2.243 5 5 5a4.97 4.97 0 0 0 3.423-1.375L10 17l1.577-2.366A4.97 4.97 0 0 0 15 16.01c2.757 0 5-2.243 5-5 0-1.112-.377-2.13-.993-2.96.166-.7.565-1.62.993-2.04z" />
                      </svg>
                    </div>
                    <span className="font-medium">TripAdvisor</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">Apple Maps</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 rounded-full bg-[#0061A8] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">BBB</span>
                    </div>
                    <span className="font-medium">Better Business Bureau</span>
                  </button>

                  <button
                    type="button"
                    className="w-full flex items-center gap-3 cursor-pointer py-3 px-4 rounded-lg text-base hover:bg-gray-100 transition-colors text-gray-400">
                    <div className="h-8 w-8 flex items-center justify-center text-[#FF1A1A]">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 16 16"
                        fill="currentColor">
                        <path d="m4.188 10.095.736-.17.073-.02A.813.813 0 0 0 5.45 8.65a1 1 0 0 0-.3-.258 3 3 0 0 0-.428-.198l-.808-.295a76 76 0 0 0-1.364-.493C2.253 7.3 2 7.208 1.783 7.14c-.041-.013-.087-.025-.124-.038a2.1 2.1 0 0 0-.606-.116.72.72 0 0 0-.572.245 2 2 0 0 0-.105.132 1.6 1.6 0 0 0-.155.309c-.15.443-.225.908-.22 1.376.002.423.013.966.246 1.334a.8.8 0 0 0 .22.24c.166.114.333.129.507.141.26.019.513-.045.764-.103l2.447-.566z" />
                      </svg>
                    </div>
                    <span className="font-medium">Yelp</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-6 lg:px-8 pb-6">
          <p className="text-sm text-muted-foreground">
            Facebook connection is now managed from Business Profile.
          </p>
        </CardContent>
      </Card>

      {SEO_AUDIT_ENABLED === 'true' && (
        <Card className="shadow-md rounded-2xl border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-4 pt-6 px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Star className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <CardTitle className="text-xl lg:text-2xl font-bold">
                  Review Platforms
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  Connect platforms to monitor and manage reviews
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-6 lg:px-8 pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {REVIEW_PLATFORMS.map((platform) => {
                const platformData = reviewPlatforms[platform.key] || {};
                const connected = platform.isConnected(platformData);
                const previewUrl = platform.getPreviewUrl(platformData);

                return (
                  <div
                    key={platform.key}
                    className={`p-5 rounded-xl border-2 bg-background transition-colors ${
                      connected
                        ? 'border-border hover:border-primary/30'
                        : 'border-dashed border-border/60 hover:border-primary/40 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!connected) openConfigModal(platform);
                    }}>
                    <div className="flex items-center gap-4 mb-4">
                      <div
                        className={`h-14 w-14 rounded-full ${platform.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                        {platform.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-lg">{platform.name}</p>
                        {connected && previewUrl ? (
                          <a
                            className="text-sm text-muted-foreground truncate block hover:text-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={previewUrl}
                            onClick={(e) => e.stopPropagation()}>
                            {previewUrl.length > 40
                              ? previewUrl.substring(0, 40) + '...'
                              : previewUrl}
                          </a>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {connected ? 'Connected' : 'Click to configure'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      {connected ? (
                        <>
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                            <CheckCircle2 className="h-4 w-4 mr-1.5" />
                            Connected
                          </Badge>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-2 rounded-lg text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openConfigModal(platform);
                              }}>
                              <ExternalLink className="h-3.5 w-3.5" />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 rounded-lg"
                              style={{
                                background: 'none',
                                cursor: 'pointer',
                                outline: 'inherit'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setReviewDisconnectPlatform(platform);
                                setReviewDisconnectModalOpen(true);
                              }}>
                              <Unplug className="h-3.5 w-3.5" />
                              Disconnect
                            </Button>
                          </div>
                        </>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 border-0 px-3 py-1.5 text-sm font-medium rounded-full transition-colors">
                          <X className="h-4 w-4 mr-1.5" /> Disconnected
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Review Platform Configuration Modal ───────────── */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              {configPlatform && (
                <div
                  className={`h-10 w-10 rounded-full ${configPlatform.color} flex items-center justify-center shadow-md flex-shrink-0`}>
                  {configPlatform.icon}
                </div>
              )}
              Configure {configPlatform?.name}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-1">
              Enter the details below to connect your {configPlatform?.name}{' '}
              listing.
            </DialogDescription>
          </DialogHeader>

          {configPlatform && (
            <div className="space-y-5 mt-2">
              {/* How to find instructions */}
              <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                <p className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  How to find your {configPlatform.name} identifier
                </p>
                <p className="text-sm text-blue-700">
                  {configPlatform.howToFind}
                </p>
              </div>

              {/* Form fields */}
              {configPlatform.fields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label className="text-sm font-semibold">
                    {field.label}
                    {field.required && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  {field.type === 'select' ? (
                    <select
                      value={configFormData[field.key] || ''}
                      onChange={(e) =>
                        handleConfigFieldChange(field.key, e.target.value)
                      }
                      className="flex h-12 w-full rounded-xl border-2 border-input bg-background px-4 py-2 text-sm ring-offset-background focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="" disabled>
                        Select {field.label}...
                      </option>
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={configFormData[field.key] || ''}
                      onChange={(e) =>
                        handleConfigFieldChange(field.key, e.target.value)
                      }
                      placeholder={field.placeholder}
                      className="h-12 rounded-xl border-2 focus:border-primary px-4"
                    />
                  )}
                </div>
              ))}

              {/* Preview link */}
              {configPlatform.getPreviewUrl(configFormData) && (
                <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                  <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Preview — verify this is the correct page
                  </p>
                  <a
                    href={configPlatform.getPreviewUrl(configFormData)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-700 underline hover:text-green-900 break-all">
                    {configPlatform.getPreviewUrl(configFormData)}
                  </a>
                  <p className="text-xs text-green-600 mt-2">
                    ⚠️ Click the link above to verify it shows the correct
                    business before saving.
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setConfigModalOpen(false)}
                  disabled={isSavingPlatform}
                  className="h-11 px-6 rounded-lg">
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePlatformConfig}
                  disabled={
                    isSavingPlatform ||
                    !configPlatform.fields.some(
                      (f) => f.required && configFormData[f.key]?.trim()
                    )
                  }
                  className="h-11 px-6 rounded-lg bg-primary hover:bg-primary/90 gap-2">
                  {isSavingPlatform ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save & Connect
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Review Platform Disconnect Confirm Modal ──────── */}
      <Dialog
        open={reviewDisconnectModalOpen}
        onOpenChange={setReviewDisconnectModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Disconnect {reviewDisconnectPlatform?.name}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Are you sure you want to disconnect{' '}
              {reviewDisconnectPlatform?.name}? This will remove all saved
              configuration for this platform.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-destructive hover:bg-destructive/90 text-white px-8 h-11 text-base font-semibold rounded-lg"
              onClick={handleDisconnectReviewPlatform}
              disabled={isDisconnectingReview}>
              {isDisconnectingReview ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setReviewDisconnectModalOpen(false)}
              disabled={isDisconnectingReview}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Modal */}
      <Dialog open={disconnectModalOpen} onOpenChange={setDisconnectModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              {`Disconnect ${
                disconnectNetwork === 'googleMyBusiness'
                  ? 'Google'
                  : disconnectNetwork.charAt(0).toUpperCase() +
                    disconnectNetwork.slice(1)
              }`}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              {`Are you sure you want disconnect ${
                disconnectNetwork === 'googleMyBusiness'
                  ? 'google'
                  : disconnectNetwork
              }?`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDisconnect}
              disabled={isDisconnecting}>
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'OK'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDisconnectModalOpen(false)}
              disabled={isDisconnecting}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={handleSuccessClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center">
              Disconnected!
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button onClick={handleSuccessClose} className="h-11 px-8">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <Dialog open={errorModalOpen} onOpenChange={setErrorModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-2xl font-bold text-center text-red-600">
              Disconnection Failed
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center mt-2">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => setErrorModalOpen(false)}
              variant="outline"
              className="h-11 px-8">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Links;
